'use client';

import { useCallback, useEffect, useState, useRef } from 'react';
import clsx from 'classnames';

import styles from './RangeSlider.module.scss';
import { TextField } from '../textfield';
import { RangeSliderProps } from './types';

export const RangeSlider = ({
	suffix,
	fromLabel = 'Od',
	toLabel = 'Do',
	min = 1,
	max = 10,
	minVal: initialMinVal,
	maxVal: initialMaxVal,
	onChange,
	onBlur,
}: RangeSliderProps) => {
	const [minVal, setMinVal] = useState(initialMinVal ?? min);
	const [maxVal, setMaxVal] = useState(initialMaxVal ?? max);
	const [minInput, setMinInput] = useState(String(initialMinVal ?? min));
	const [maxInput, setMaxInput] = useState(String(initialMaxVal ?? max));
	const minValRef = useRef(initialMinVal ?? min);
	const maxValRef = useRef(initialMaxVal ?? max);
	const range = useRef<HTMLDivElement>(null);

	const getPercent = useCallback(
		(value: number) => Math.round(((value - min) / (max - min)) * 100),
		[min, max]
	);

	const handleLeftThumbChange = (value: string) => {
		const numberValue = Math.min(Number(value), maxVal - 1);
		const minPercent = getPercent(numberValue) < 0 ? 0 : getPercent(numberValue);
		const maxPercent = getPercent(maxValRef.current);

		if (range.current) {
			range.current.style.left = `${minPercent}%`;
			range.current.style.width = `${maxPercent - minPercent}%`;
		}

		setMinVal(numberValue);
		minValRef.current = numberValue;
		setMinInput(numberValue.toString());

		onChange && onChange({ min: numberValue, max: maxVal });
	};

	const handleRightThumbChange = (value: string) => {
		const numberValue = Math.max(Number(value), minVal + 1);
		const minPercent = getPercent(minValRef.current);
		const maxPercent = getPercent(numberValue) > 100 ? 100 : getPercent(numberValue);

		if (range.current) {
			range.current.style.width = `${maxPercent - minPercent}%`;
		}

		setMaxVal(numberValue);
		maxValRef.current = numberValue;
		setMaxInput(numberValue.toString());

		onChange && onChange({ min: minVal, max: numberValue });
	};

	const handleMinInputChange = (value: string) => {
		const number = Number(value);

		if (number < min) {
			handleLeftThumbChange(min.toString());
			return;
		}

		if (number >= maxVal) {
			handleLeftThumbChange((maxVal - 1).toString());
			return;
		}

		const numberValue = Math.min(Number(value), maxVal - 1);
		onBlur?.({ min: numberValue, max: maxVal });
		handleLeftThumbChange(value);
	};

	const handleMaxInputChange = (value: string) => {
		const number = Number(value);

		if (number > max) {
			handleRightThumbChange(max.toString());
			return;
		}

		if (number <= minVal) {
			handleRightThumbChange((minVal + 1).toString());
			return;
		}

		const numberValue = Math.min(Number(value), maxVal - 1);
		onBlur?.({ min: minVal, max: numberValue });
		handleRightThumbChange(value);
	};

	useEffect(() => {
		const minPercent = getPercent(minValRef.current);
		const maxPercent = getPercent(maxValRef.current);

		if (range.current) {
			range.current.style.left = `${minPercent}%`;
			range.current.style.width = `${maxPercent - minPercent}%`;
		}
	}, []);

	return (
		<>
			<div className={styles.rangeSlider}>
				<input
					type="range"
					min={min}
					max={max}
					value={minVal}
					onChange={(event) => handleLeftThumbChange(event.target.value)}
					className={clsx(styles.rangeSlider__thumb, styles['rangeSlider__thumb--left'])}
					style={{ zIndex: minVal > max - 100 ? '5' : '3' }}
					onMouseUp={() => onBlur?.({ min: minVal, max: maxVal })}
					onTouchEnd={() => onBlur?.({ min: minVal, max: maxVal })}
				/>
				<input
					type="range"
					min={min}
					max={max}
					value={maxVal}
					onChange={(event) => handleRightThumbChange(event.target.value)}
					className={clsx(styles.rangeSlider__thumb, styles['rangeSlider__thumb--right'])}
					onMouseUp={() => onBlur?.({ min: minVal, max: maxVal })}
					onTouchEnd={() => onBlur?.({ min: minVal, max: maxVal })}
				/>

				<div className={styles.rangeSlider__slider}>
					<div className={styles.rangeSlider__slider__track} />
					<div ref={range} className={styles.rangeSlider__slider__range} />
				</div>
			</div>
			<div className={styles.rangeSlider__values}>
				<TextField
					label={fromLabel}
					value={minInput}
					suffix={suffix}
					type="number"
					onBlur={(e) => handleMinInputChange(e.target.value)}
					onChange={(e) => setMinInput(e.target.value)}
				/>
				<span>-</span>
				<TextField
					label={toLabel}
					value={maxInput}
					suffix={suffix}
					type="number"
					onBlur={(e) => handleMaxInputChange(e.target.value)}
					onChange={(e) => setMaxInput(e.target.value)}
				/>
			</div>
		</>
	);
};
